import { useState, useCallback, useRef, useEffect, useMemo } from 'react';
import { useNotifications } from '@contexts/NotificationContext';
import { useActionSnackbar } from '@contexts/ActionSnackbarContext';
import { useGenericUndoRedo } from './useGenericUndoRedo';
import { useLabsHotkeys } from './useLabsHotkeys';
import { PlaybookTransformType } from '@/types/playbook';
import { 
  getActiveEditorId, 
  setActiveEditorId, 
  clearOtherLabsEditorStorage,
  clearOtherLabsUIStorage 
} from './useLabsPersistence';
import isEqual from 'lodash-es/isEqual';
import omit from 'lodash-es/omit';

// Helper to capitalize the first letter of a string
const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1);
};

// Generic editor state interface that all labs editors must implement
export interface BaseEditorState {
  id: string;
  isNew: boolean;
  isSubmitting: boolean;
  status: 'saved' | 'draft' | 'published';
  lastSaved?: number;
  version?: string;
}

// Configuration for different editor types
export interface EditorConfig<T extends BaseEditorState> {
  editorType: 'playbook' | 'schema' | 'integration' | 'contract';
  storageKeyPrefix: string;
  maxHistory?: number;
  autoSaveInterval?: number;
  enableHotkeys?: boolean;
  enableUndoRedo?: boolean;
  validateState?: (state: T) => { isValid: boolean; warnings: string[] };
  onSave?: (state: T) => Promise<T>;
  onLoad?: (id: string) => Promise<T | null>;
}

// Persistence conflict resolution options
export interface PersistenceConflict<T> {
  backendState: T;
  persistedState: T;
  timestamp: number;
}

// Helper function to compare states excluding metadata fields
const compareContentStates = <T extends BaseEditorState>(state1: T, state2: T): boolean => {
  // Fields to exclude from comparison (metadata that doesn't affect content)
  const excludeFields = [
    'lastSaved',
    'isSubmitting',
    'isNew',
    'isNewPlaybook', // Playbook-specific field
    'activeTab', // UI state field
  ];
  
  const cleanState1 = omit(state1, excludeFields);
  const cleanState2 = omit(state2, excludeFields);
  
  return isEqual(cleanState1, cleanState2);
};

// Main hook for unified labs editor functionality
export function useLabsEditor<T extends BaseEditorState>(
  initialState: T,
  config: EditorConfig<T>
) {
  const { showNotification } = useNotifications();
  const { showSnackbar } = useActionSnackbar();
  
  // Core state management with undo/redo
  const {
    state,
    takeSnapshot,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useGenericUndoRedo<T>(initialState, config.maxHistory || 50);

  // Refs for stable access
  const stateRef = useRef(state);
  const initialStateRef = useRef<T | null>(null);
  
  // Local state
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [persistenceConflict, setPersistenceConflict] = useState<PersistenceConflict<T> | null>(null);
  const [isResolvingConflict, setIsResolvingConflict] = useState(false);
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);

  // Update refs when state changes
  useEffect(() => {
    stateRef.current = state;
  }, [state]);

  // Storage key management
  const getStorageKey = useCallback((id: string, suffix?: string) => {
    const base = `${config.storageKeyPrefix}-${id}`;
    return suffix ? `${base}-${suffix}` : base;
  }, [config.storageKeyPrefix]);

  // Persistence functions
  const saveToStorage = useCallback((stateToSave: T) => {
    try {
      const storageKey = getStorageKey(stateToSave.id);
      const persistedData = {
        data: stateToSave,
        timestamp: Date.now(),
        version: '1.0.0',
        editorType: config.editorType,
      };
      localStorage.setItem(storageKey, JSON.stringify(persistedData));
    } catch (error) {
      console.error(`[${config.editorType}] Failed to save to storage:`, error);
    }
  }, [getStorageKey, config.editorType]);

  const loadFromStorage = useCallback((id: string): T | null => {
    try {
      const storageKey = getStorageKey(id);
      const saved = localStorage.getItem(storageKey);
      if (!saved) return null;

      const parsed = JSON.parse(saved);
      if (parsed.editorType !== config.editorType) {
        console.warn(`[${config.editorType}] Storage type mismatch for ${id}`);
        return null;
      }

      return parsed.data as T;
    } catch (error) {
      console.error(`[${config.editorType}] Failed to load from storage:`, error);
      return null;
    }
  }, [getStorageKey, config.editorType]);

  const clearStorage = useCallback((id?: string) => {
    try {
      if (id) {
        const storageKey = getStorageKey(id);
        localStorage.removeItem(storageKey);
      } else {
        // Clear all storage for this editor type
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key?.startsWith(config.storageKeyPrefix)) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
      }
    } catch (error) {
      console.error(`[${config.editorType}] Failed to clear storage:`, error);
    }
  }, [getStorageKey, config.storageKeyPrefix, config.editorType]);

  // State update function that triggers snapshots
  const updateState = useCallback((updates: Partial<T> | ((prevState: T) => T)) => {
    if (typeof updates === 'function') {
      const newState = updates(stateRef.current);
      takeSnapshot(newState);
    } else {
      const newState = { ...stateRef.current, ...updates };
      takeSnapshot(newState);
    }
  }, [takeSnapshot]);

  // Auto-save to localStorage (only after initial load is complete)
  useEffect(() => {
    if (hasInitiallyLoaded && !state.isNew && !state.isSubmitting && state.id !== 'new') {
      console.log(`[${config.editorType}] Auto-saving state to localStorage`);
      saveToStorage(state);
    }
  }, [state, saveToStorage, hasInitiallyLoaded, config.editorType]);

  // Track unsaved changes
  useEffect(() => {
    if (initialStateRef.current) {
      const hasChanges = !isEqual(state, initialStateRef.current);
      setHasUnsavedChanges(hasChanges);
    }
  }, [state]);

  // Validation
  const validationResult = useMemo(() => {
    if (config.validateState) {
      return config.validateState(state);
    }
    return { isValid: true, warnings: [] };
  }, [state, config.validateState]);

  // Load state function
  const loadState = useCallback(async (id: string, forceBackend = false) => {
    console.log(`[${config.editorType}] Loading state for ID: "${id}", forceBackend: ${forceBackend}, isResolvingConflict: ${isResolvingConflict}`);
    setIsLoading(true);
    
    try {
      // Single-state persistence: Clear other editor storage when loading a new editor
      const currentActiveEditorId = getActiveEditorId();
      if (currentActiveEditorId && currentActiveEditorId !== id && id !== 'new') {
        console.log(`[${config.editorType}] Switching from editor "${currentActiveEditorId}" to "${id}", clearing other storage`);
        clearOtherLabsEditorStorage(id, config.editorType);
        clearOtherLabsUIStorage(id);
      }
      
      // Set this editor as the active one (unless it's a new editor)
      if (id !== 'new') {
        setActiveEditorId(id);
        console.log(`[${config.editorType}] Set active editor ID to: "${id}"`);
      }

      // For new items, clear any existing storage and use initial state
      if (id === 'new') {
        console.log(`[${config.editorType}] New item detected, clearing storage and using initial state`);
        clearStorage();
        initialStateRef.current = state;
        setIsLoading(false);
        return;
      }

      // Load from backend if available
      let backendState: T | null = null;
      let backendLoadFailed = false;
      if (config.onLoad) {
        try {
          console.log(`[${config.editorType}] Loading from backend...`);
          backendState = await config.onLoad(id);
          console.log(`[${config.editorType}] Backend state loaded:`, backendState ? 'SUCCESS' : 'NULL');
        } catch (error: any) {
          console.error(`[${config.editorType}] Failed to load from backend:`, error);
          backendLoadFailed = true;
          
          // If it's a 404, the item doesn't exist in backend - this is OK if we have persisted data
          // For other errors, show notification and exit
          if (error?.response?.status !== 404) {
            showNotification('error', `Failed to load ${config.editorType}.`);
            setIsLoading(false);
            return;
          }
        }
      }

      // Load from localStorage
      console.log(`[${config.editorType}] Loading from localStorage...`);
      const persistedState = loadFromStorage(id);
      console.log(`[${config.editorType}] Persisted state loaded:`, persistedState ? 'SUCCESS' : 'NULL');

      // Handle conflicts (but not if we're already resolving one)
      if (backendState && persistedState && !forceBackend && !isResolvingConflict) {
        console.log(`[${config.editorType}] Checking for conflicts between backend and persisted state...`);
        const areStatesEqual = compareContentStates(backendState, persistedState);
        console.log(`[${config.editorType}] States are equal:`, areStatesEqual);
        
        if (!areStatesEqual) {
          console.log(`[${config.editorType}] Conflict detected! Setting persistence conflict dialog.`);
          setPersistenceConflict({
            backendState,
            persistedState,
            timestamp: Date.now(),
          });
          setIsLoading(false);
          return;
        }
      }

      // Use appropriate state
      const stateToUse = forceBackend ? backendState : (persistedState || backendState);
      console.log(`[${config.editorType}] State to use:`, stateToUse ? 'FOUND' : 'NULL', `(forceBackend: ${forceBackend})`);
      
      if (stateToUse) {
        takeSnapshot(stateToUse);
        initialStateRef.current = stateToUse;
        setHasUnsavedChanges(false);
        
        // If we have persisted data but no backend data (404), mark as unsaved
        if (persistedState && (!backendState || backendLoadFailed)) {
          console.log(`[${config.editorType}] Marking as unsaved (persisted data without backend)`);
          setHasUnsavedChanges(true);
        }
      } else if (backendLoadFailed && !persistedState) {
        // If backend failed and no persisted data, show error
        console.log(`[${config.editorType}] No data found anywhere, showing error`);
        showNotification('error', `${config.editorType} not found.`);
      }

    } finally {
      setIsLoading(false);
      setHasInitiallyLoaded(true);
    }
  }, [state, config, loadFromStorage, clearStorage, takeSnapshot, showNotification, isResolvingConflict]);

  // Save function
  const saveState = useCallback(async () => {
    if (!config.onSave) {
      showNotification('error', 'Save function not configured.');
      return false;
    }

    updateState({ isSubmitting: true } as Partial<T>);
    
    try {
      const savedState = await config.onSave(stateRef.current);
      
      takeSnapshot(savedState);
      initialStateRef.current = savedState;
      setHasUnsavedChanges(false);
      
      // Clear storage since we now have the saved version in backend
      clearStorage(savedState.id);
      
      showNotification('success', `${capitalizeFirstLetter(config.editorType)} saved successfully.`);
      return true;
    } catch (error) {
      console.error(`[${config.editorType}] Save failed:`, error);
      showNotification('error', `Failed to save ${config.editorType}.`);
      return false;
    } finally {
      updateState({ isSubmitting: false } as Partial<T>);
    }
  }, [config, updateState, takeSnapshot, clearStorage, showNotification]);

  // Conflict resolution
  const resolveConflict = useCallback((useBackend: boolean) => {
    if (!persistenceConflict || isResolvingConflict) {
      return;
    }

    setIsResolvingConflict(true);
    
    const stateToUse = useBackend ? persistenceConflict.backendState : persistenceConflict.persistedState;
    
    // Clear the conflict first to prevent re-detection
    setPersistenceConflict(null);
    
    // Update the state
    takeSnapshot(stateToUse);
    
    // Set unsaved changes state and initial reference
    if (useBackend) {
      // When loading from backend, set as baseline and mark as saved
      initialStateRef.current = stateToUse;
      clearStorage(stateToUse.id);
      setHasUnsavedChanges(false);
      showNotification('info', 'Loaded last saved version.');
    } else {
      // When loading persisted changes, keep backend as baseline so changes are detected
      initialStateRef.current = persistenceConflict.backendState;
      setHasUnsavedChanges(true);
      showNotification('info', 'Loaded unsaved changes.');
    }
    
    // Reset the resolving flag after a short delay
    setTimeout(() => {
      setIsResolvingConflict(false);
    }, 100);
  }, [persistenceConflict, isResolvingConflict, takeSnapshot, clearStorage, showNotification, config.editorType]);

  // Undo/Redo with notifications
  const handleUndo = useCallback(() => {
    undo();
    showSnackbar('Undo');
  }, [undo, showSnackbar]);

  const handleRedo = useCallback(() => {
    redo();
    showSnackbar('Redo');
  }, [redo, showSnackbar]);

  // Hotkeys setup (placeholder functions for non-playbook editors)
  const noOpFunction = useCallback(() => {}, []);
  const noOpTransformFunction = useCallback((_type: PlaybookTransformType) => {}, []);

  // Setup hotkeys if enabled
  const hotkeysEnabled = config.enableHotkeys !== false;
  useLabsHotkeys(
    hotkeysEnabled ? handleUndo : noOpFunction,
    hotkeysEnabled ? handleRedo : noOpFunction,
    noOpFunction, // openInputSchema - can be overridden by specific editors
    noOpFunction, // openOutputSchema - can be overridden by specific editors  
    noOpFunction, // openYamlPreview - can be overridden by specific editors
    noOpTransformFunction, // addTransform - can be overridden by specific editors
    noOpFunction, // selectConnectionSection - can be overridden by specific editors
    noOpFunction, // selectGlobalSection - can be overridden by specific editors
  );

  // Cleanup function
  const cleanup = useCallback(() => {
    if (hasUnsavedChanges) {
      clearStorage(state.id);
    }
  }, [hasUnsavedChanges, clearStorage, state.id]);

  return {
    // State
    state,
    updateState,
    isLoading,
    hasUnsavedChanges,
    validationResult,
    persistenceConflict,
    
    // Actions
    loadState,
    saveState,
    resolveConflict,
    cleanup,
    
    // Undo/Redo
    undo: handleUndo,
    redo: handleRedo,
    canUndo,
    canRedo,
    
    // Storage
    clearStorage,
  };
}
